import { Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/utils/cn';
import { formatMoney, formatPercent } from '@/utils/format';

export const PAID_PLAN_FEATURE_KEYS = ['0', '1', '2', '3', '4'] as const;

export type CheckoutPaidPlan = 'essential' | 'professional';

export const CHECKOUT_PAID_PLANS: CheckoutPaidPlan[] = ['essential', 'professional'];

export const YEARLY_BILLING_DISCOUNT = 0.2;

const LANDING_BILLING_CURRENCY = 'GHS';

const PAID_PLAN_BILLING: Record<
  CheckoutPaidPlan,
  { monthlyPerSeatMajor: number; yearlyPerSeatMajor: number; includedSeats: number }
> = {
  essential: {
    monthlyPerSeatMajor: 50,
    yearlyPerSeatMajor: 50 * (1 - YEARLY_BILLING_DISCOUNT),
    includedSeats: 8,
  },
  professional: {
    monthlyPerSeatMajor: 100,
    yearlyPerSeatMajor: 100 * (1 - YEARLY_BILLING_DISCOUNT),
    includedSeats: 15,
  },
};

const toMinor = (major: number): number => Math.round(major * 100);

export type PaidPlanBillingInterval = 'monthly' | 'yearly';

export const toLandingBillingInterval = (interval: 'monthly' | 'annual'): PaidPlanBillingInterval =>
  interval === 'annual' ? 'yearly' : 'monthly';

export const perSeatPriceMinor = (
  plan: CheckoutPaidPlan,
  interval: PaidPlanBillingInterval,
): number => {
  const billing = PAID_PLAN_BILLING[plan];
  const major =
    interval === 'monthly' ? billing.monthlyPerSeatMajor : billing.yearlyPerSeatMajor;
  return toMinor(major);
};

export const PaidPlanOfferingCard = ({
  plan,
  interval,
  featured = plan === 'professional',
  isCurrent = false,
  currentPlanBadge,
  footer,
  className,
}: {
  plan: CheckoutPaidPlan;
  interval: PaidPlanBillingInterval;
  featured?: boolean;
  isCurrent?: boolean;
  currentPlanBadge?: string;
  footer: ReactNode;
  className?: string;
}) => {
  const { t } = useTranslation('landing');
  const perSeatMinor = perSeatPriceMinor(plan, interval);

  return (
    <article
      className={cn(
        'relative flex h-full flex-col rounded-2xl border bg-surface p-5 text-fg sm:p-6',
        isCurrent
          ? 'border-brand ring-2 ring-brand/15'
          : featured
            ? 'border-brand/40 ring-1 ring-brand/10'
            : 'border-border',
        className,
      )}
    >
      {featured && !isCurrent ? (
        <span className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 rounded-full border border-surface bg-brand px-3 py-0.5 text-caption font-semibold text-on-brand">
          {t('pricing.popular')}
        </span>
      ) : null}

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-card-title font-semibold text-fg">{t(`pricing.plans.${plan}.name`)}</h3>
          {isCurrent && currentPlanBadge ? <Badge variant="brand">{currentPlanBadge}</Badge> : null}
        </div>
        <p className="text-pretty text-body-sm leading-relaxed text-fg-secondary">
          {t(`pricing.plans.${plan}.body`)}
        </p>
      </div>

      <div className="mt-5 space-y-1 border-b border-border pb-5">
        <p className="text-section-title font-semibold text-brand">
          {formatMoney(perSeatMinor, LANDING_BILLING_CURRENCY)}
        </p>
        <p className="text-body-sm text-fg-secondary">{t(`pricing.plans.${plan}.period.${interval}`)}</p>
      </div>

      <ul className="mt-5 grid flex-1 gap-2.5">
        {PAID_PLAN_FEATURE_KEYS.map((featureKey) => (
          <li key={featureKey} className="flex gap-3 text-body-sm leading-snug text-fg-secondary">
            <Check className="mt-0.5 size-4 shrink-0 text-brand" aria-hidden />
            <span>{t(`pricing.plans.${plan}.features.${featureKey}`)}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-6">{footer}</div>
    </article>
  );
};

export const PaidPlanBillingToggle = ({
  yearly,
  onYearlyChange,
  className,
}: {
  yearly: boolean;
  onYearlyChange: (yearly: boolean) => void;
  className?: string;
}) => {
  const { t } = useTranslation('landing');

  return (
    <div className={cn('flex flex-col items-center gap-2', className)}>
      <div
        className="flex flex-col items-center gap-3 sm:flex-row sm:gap-3"
        role="group"
        aria-label={t('pricing.billingToggleAria')}
      >
        <span
          className={cn(
            'text-body-sm font-medium transition-colors',
            !yearly ? 'text-fg' : 'text-fg-secondary',
          )}
        >
          {t('pricing.billingMonthly')}
        </span>
        <Switch checked={yearly} onCheckedChange={onYearlyChange} aria-label={t('pricing.billingToggleAria')} />
        <span
          className={cn(
            'flex flex-wrap items-center justify-center gap-2 text-body-sm font-medium transition-colors',
            yearly ? 'text-fg' : 'text-fg-secondary',
          )}
        >
          {t('pricing.billingYearly')}
          <span className="rounded-full border border-brand/20 bg-brand/10 px-2 py-0.5 text-caption font-semibold text-brand">
            {t('pricing.savePercent', { percent: formatPercent(YEARLY_BILLING_DISCOUNT) })}
          </span>
        </span>
      </div>
    </div>
  );
};
